import { useEffect, useState } from "react";
import { CornerMarks } from "@/components/infra";
import { OUTLETS } from "@/sections/InTheNews";

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const useCountUp = (target, run, duration = 1300) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!run || typeof target !== "number") return undefined;
    if (reduced()) { setValue(target); return undefined; }
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      setValue(target * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, run, duration]);
  return value;
};

const useReady = (run) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!run) return undefined;
    const frame = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(frame);
  }, [run]);
  return ready;
};

const Number_ = ({ value, decimals = 0, suffix = "" }) => (
  <span className="block font-display text-2xl sm:text-3xl font-bold text-[#0F1117] whitespace-nowrap tabular-nums">
    {value === null ? "—" : `${value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`}
  </span>
);

const RolesBars = ({ sources, ready }) => {
  const top = [...sources].sort((a, b) => b.count - a.count).slice(0, 4);
  const max = Math.max(1, ...top.map((source) => source.count));
  return (
    <ul aria-label="Top employers by open roles" className="space-y-1">
      {top.map((source, index) => (
        <li key={source.id} className="text-[10px] text-[#3D4049]">
          <span className="flex items-center justify-between gap-2"><span className="truncate">{source.name}</span><span className="tabular-nums text-[#5F6168]">{source.count}{source.capped ? "+" : ""}</span></span>
          <span className="mt-0.5 block h-1.5 bg-[#E8EAEE] overflow-hidden"><span className="block h-full bg-[#0F1117] origin-left transition-transform duration-1000 ease-out" style={{ transform: `scaleX(${ready ? source.count / max : 0})`, transitionDelay: `${index * 90}ms` }} /></span>
        </li>
      ))}
    </ul>
  );
};

const FeedGrid = ({ sources, ready }) => (
  <ul aria-label="Employer feed status" className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-12 gap-1">
    {sources.map((source, index) => (
      <li key={source.id} title={`${source.name}: ${source.status === "current" ? "live" : source.status}`} className={`aspect-square transition-[opacity,transform] duration-500 ${source.status === "current" ? "bg-[#10B981] shadow-[0_0_6px_rgba(16,185,129,0.55)]" : "bg-[#F5A524]"} ${ready ? "opacity-100 scale-100" : "opacity-0 scale-50"}`} style={{ transitionDelay: `${index * 35}ms` }}>
        <span className="sr-only">{source.name} {source.status === "current" ? "live" : source.status}</span>
      </li>
    ))}
  </ul>
);

const EarlyRing = ({ early, total, ready }) => {
  const share = total ? early / total : 0;
  const circumference = 2 * Math.PI * 15.9;
  return (
    <div className="flex items-center gap-3">
      <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90 shrink-0" aria-hidden="true">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E8EAEE" strokeWidth="3.2" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#10B981" strokeWidth="3.2" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={ready ? circumference * (1 - share) : circumference} className="transition-[stroke-dashoffset] duration-[1200ms] ease-out" />
      </svg>
      <span className="text-[10px] leading-snug text-[#3D4049]"><span className="block font-display text-base font-bold text-[#078552]">{(share * 100).toFixed(1)}%</span>of all open roles</span>
    </div>
  );
};

const ReachMeter = () => {
  const ready = useReady(true);
  return (
  <div>
    <div className="relative h-2 bg-[#E8EAEE] overflow-hidden" aria-hidden="true">
      <span className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0F1117] via-[#5F6168] to-[#10B981] origin-left transition-transform duration-[1400ms] ease-out w-[80%]" style={{ transform: `scaleX(${ready ? 1 : 0})` }} />
    </div>
    <div className="mt-1 flex justify-between text-[9px] text-[#71747B] tabular-nums" aria-hidden="true"><span>0</span><span>50M</span><span>100M</span><span>150M</span></div>
    <ul aria-label="Outlets" className="mt-2 flex flex-wrap items-center gap-1.5">
      {OUTLETS.map((outlet) => (
        <li key={outlet.name} title={outlet.name} className="w-5 h-5 bg-white border border-[#E3E5E9] flex items-center justify-center">
          <img src={`https://www.google.com/s2/favicons?domain=${outlet.domain}&sz=64`} alt={outlet.name} loading="lazy" className="w-3.5 h-3.5 object-contain" />
        </li>
      ))}
    </ul>
  </div>
  );
};

const StatCard = ({ testId, label, note, number, children, className = "" }) => (
  <li data-testid={testId} className={`min-w-0 flex flex-col gap-3 px-4 sm:px-5 py-5 border-[#E3E5E9] ${className}`}>
    <span>
      {number}
      <span className="block mt-1.5 font-mono-label text-[10px] !tracking-normal text-[#3D4049]">{label}</span>
    </span>
    <div className="min-h-[58px]">{children}</div>
    <span className="mt-auto block text-[10px] text-[#71747B]">{note}</span>
  </li>
);

export const StatsVisualStrip = ({ jobs, updated, prefix }) => {
  const run = Boolean(jobs);
  const ready = useReady(run);
  const sources = jobs?.sources || [];
  const roles = useCountUp(jobs?.feed_total, run);
  const feeds = useCountUp(jobs?.employer_count, run);
  const early = useCountUp(jobs?.early_career_total, run);
  const reach = useCountUp(119.8, true, 1500);
  const live = sources.filter((source) => source.status === "current").length;
  return (
    <div data-testid={`${prefix}-stats`} className="chrome-surface relative fade-up delay-300">
      <CornerMarks color="#A9ADB4" />
      <ul aria-label="Live industry stats" className="grid grid-cols-2 lg:grid-cols-4">
        <StatCard testId={`${prefix}-stat-roles`} className="border-r border-b lg:border-b-0" number={<Number_ value={run ? Math.round(roles) : null} />} label="Open industry roles" note="Live job feed · top employers">
          <RolesBars sources={sources} ready={ready} />
        </StatCard>
        <StatCard testId={`${prefix}-stat-employers`} className="border-b lg:border-b-0 lg:border-r" number={<Number_ value={run ? Math.round(feeds) : null} />} label="Employer feeds" note={sources.length ? `${live} of ${sources.length} feeds live now` : "Live careers sources"}>
          <FeedGrid sources={sources} ready={ready} />
        </StatCard>
        <StatCard testId={`${prefix}-stat-early`} className="border-r" number={<Number_ value={run ? Math.round(early) : null} />} label="Early-career roles" note="Internships, apprenticeships, entry level">
          <EarlyRing early={jobs?.early_career_total || 0} total={jobs?.feed_total || 0} ready={ready} />
        </StatCard>
        <StatCard testId={`${prefix}-stat-reach`} number={<Number_ value={reach} decimals={1} suffix="M" />} label="Media reach / month" note="GlobeNewswire 2026">
          <ReachMeter />
        </StatCard>
      </ul>
      <p data-testid={`${prefix}-updated`} className="px-5 py-2.5 border-t hairline font-mono-label text-[10px] !tracking-normal text-[#5F6168]">{updated ? `Live · Updated ${updated}` : "Live industry snapshot"}</p>
    </div>
  );
};
